import React, { useContext, useEffect, useState } from 'react';
import { SessionContext } from '../SessionContext';
import { usePopupState } from './usePopupState';
import { useLogin } from './useLogin';
import { useSearch } from './useSearch';
import { useAliasOperations } from './useAliasOperations';
import PopupLoadingScreen from './PopupLoadingScreen';
import PopupLoginForm from './PopupLoginForm';
import PopupAliasForm from './PopupAliasForm';
import PopupAliasListItem from './PopupAliasListItem';
import PopupStorageError from './PopupStorageError';
import { detectStorageAccess, getStorageErrorHelp } from './storageUtils';
import './PopupStyles.css';

const PopupLookup = () => {
    const { isLoggedIn, user, accessToken, actions, isReady } = useContext(SessionContext);
    const [storageError, setStorageError] = useState(null);

    // Add popup mode class to body on mount, remove on unmount
    useEffect(() => {
        document.body.classList.add('popup-mode');
        return () => {
            document.body.classList.remove('popup-mode');
        };
    }, []);

    // Check storage access on mount
    useEffect(() => {
        const storageCheck = detectStorageAccess();
        if (!storageCheck.hasAccess) {
            const errorHelp = getStorageErrorHelp(storageCheck);
            setStorageError(errorHelp);
            console.error('Storage access blocked:', storageCheck);
        }
    }, []);
    
    // Custom hooks
    const popupState = usePopupState(isReady);
    const loginHook = useLogin(actions);
    const aliasOps = useAliasOperations(accessToken);
    const searchHook = useSearch(aliasOps.allAliases, popupState.url, aliasOps.lookupExistingAliases);

    // Load aliases and user email addresses when logged in and ready
    useEffect(() => {
        if (isLoggedIn && accessToken && popupState.url && !popupState.initializing) {
            popupState.setLoading(true);
            aliasOps.lookupExistingAliases(popupState.url).finally(() => {
                popupState.setLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, accessToken, popupState.url, popupState.initializing]);


    // Handle search results
    useEffect(() => {
        const handleSearchResults = async () => {
            if (searchHook.search === '') {
                const results = searchHook.clearSearch();
                if (results) {
                    aliasOps.updateExistingAliases(results);
                }
            }
        };
        handleSearchResults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchHook.search]);

    // Actions
    const handleCreateAlias = async () => {
        popupState.setCreating(true);
        try {
            const success = await aliasOps.createAlias({
                site_name: popupState.siteName,
                site_url: popupState.siteUrl,
                private_notes: popupState.privateNotes,
                alerts_enabled: popupState.alertsEnabled,
                hidden: popupState.hidden,
                blacklist_action: popupState.handleSpamBy
            });
            
            if (success) {
                await aliasOps.lookupExistingAliases(popupState.siteUrl);
                popupState.setShowCreateForm(false);
            }
        } finally {
            popupState.setCreating(false);
        }
    };

    const handleSaveAlias = async () => {
        popupState.setSaving(true);
        try {
            const success = await aliasOps.saveAlias({
                id: popupState.editingAlias.id,
                site_name: popupState.siteName,
                site_url: popupState.siteUrl,
                private_notes: popupState.privateNotes,
                alerts_enabled: popupState.alertsEnabled,
                hidden: popupState.hidden,
                blacklist_action: popupState.handleSpamBy
            });
            
            if (success) {
                await aliasOps.lookupExistingAliases(popupState.url);
                popupState.cancelEdit();
            }
        } finally {
            popupState.setSaving(false);
        }
    };

    const handleEditAlias = async (alias) => {
        const fullAlias = await aliasOps.getAliasDetails(alias.id);
        if (fullAlias) {
            popupState.startEditAlias(fullAlias);
        }
    };

    const handleSearch = async (searchTerm) => {
        const results = await searchHook.handleSearch(searchTerm);
        if (results) {
            aliasOps.updateExistingAliases(results);
        }
    };

    const handleClearSearch = () => {
        const results = searchHook.clearSearch();
        aliasOps.updateExistingAliases(results);
    };

    const closePopup = () => {
        // Check if we're running in an extension iframe
        const urlParams = new URLSearchParams(window.location.search);
        const isExtension = urlParams.get('source') === 'extension';

        if (isExtension && window.parent !== window) {
            // We're in an iframe inside the extension - send message to parent
            window.parent.postMessage({ action: 'closePopup' }, '*');
        } else {
            // Regular popup window - close normally
            window.close();
        }
    };

    // Render logic
    if (storageError) {
        return <PopupStorageError errorInfo={storageError} />;
    }

    if (popupState.initializing) {
        return <PopupLoadingScreen />;
    }

    if (!isLoggedIn) {
        return (
            <PopupLoginForm 
                loginUsername={loginHook.loginUsername}
                setLoginUsername={loginHook.setLoginUsername}
                loginPassword={loginHook.loginPassword}
                setLoginPassword={loginHook.setLoginPassword}
                loggingIn={loginHook.loggingIn}
                handleLogin={loginHook.handleLogin}
            />
        );
    }

    if (popupState.showCreateForm) {
        return (
            <PopupAliasForm 
                mode="create"
                user={user}
                siteName={popupState.siteName}
                setSiteName={popupState.setSiteName}
                siteUrl={popupState.siteUrl}
                setSiteUrl={popupState.setSiteUrl}
                privateNotes={popupState.privateNotes}
                setPrivateNotes={popupState.setPrivateNotes}
                alertsEnabled={popupState.alertsEnabled}
                setAlertsEnabled={popupState.setAlertsEnabled}
                hidden={popupState.hidden}
                setHidden={popupState.setHidden}
                handleSpamBy={popupState.handleSpamBy}
                setHandleSpamBy={popupState.setHandleSpamBy}
                creating={popupState.creating}
                onSave={handleCreateAlias}
                onCancel={() => popupState.setShowCreateForm(false)}
            />
        );
    }

    if (popupState.showEditForm && popupState.editingAlias) {
        return (
            <PopupAliasForm 
                mode="edit"
                user={user}
                siteName={popupState.siteName}
                setSiteName={popupState.setSiteName}
                siteUrl={popupState.siteUrl}
                setSiteUrl={popupState.setSiteUrl}
                privateNotes={popupState.privateNotes}
                setPrivateNotes={popupState.setPrivateNotes}
                alertsEnabled={popupState.alertsEnabled}
                setAlertsEnabled={popupState.setAlertsEnabled}
                hidden={popupState.hidden}
                setHidden={popupState.setHidden}
                handleSpamBy={popupState.handleSpamBy}
                setHandleSpamBy={popupState.setHandleSpamBy}
                editingAlias={popupState.editingAlias}
                saving={popupState.saving}
                onSave={handleSaveAlias}
                onCancel={popupState.cancelEdit}
            />
        );
    }

    // Main alias list view
    return (
        <div className="popup-container">
            <div className="popup-header">
                <img src="/images/nslmail_sm.gif" alt="NslMail" className="popup-header-logo" />
                <span className="popup-header-page-name">Lookup Alias</span>
                <span className="popup-header-greeting">Hello, {user?.first_name}</span>
            </div>
            
            <div className="popup-content-padding-small">
                <div className="popup-search-container">
                    <label className="popup-search-label">Search:</label>
                    <input 
                        type="text"
                        value={searchHook.search}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="popup-search-input"
                    />
                    <button 
                        onClick={handleClearSearch}
                        className="popup-search-clear-button"
                    >
                        ✕
                    </button>
                </div>
                
                <div className="popup-alias-list-container">
                    {popupState.loading ? (
                        <div>Loading existing aliases...</div>
                    ) : (() => {
                        if (aliasOps.existingAliases.length > 0) {
                            return aliasOps.existingAliases.map((alias, index) => (
                                <PopupAliasListItem
                                    key={index}
                                    alias={alias}
                                    onEdit={handleEditAlias}
                                />
                            ));
                        } else if (searchHook.search !== '') {
                            return (
                                <div className="popup-no-results">
                                    No aliases match your search "{searchHook.search}"
                                </div>
                            );
                        } else {
                            return (
                                <div className="popup-no-results">
                                    No aliases found for {popupState.domain}
                                </div>
                            );
                        }
                    })()}
                </div>
                
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <button 
                        onClick={() => {
                            popupState.resetForm(user);
                            popupState.setShowCreateForm(true);
                        }}
                        className="popup-link-button"
                    >
                        Create new alias
                    </button>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                    <button 
                        onClick={closePopup}
                        className="popup-link-button-gray"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PopupLookup;